/**
 * @fileoverview Geobounds Selector Module - Manages the interactive modal for setting
 *               custom search area bounds using Google Maps with a draggable/resizable rectangle.
 * @module GeoboundsSelectorModule
 */
const GeoboundsSelectorModule = (function() {
  /**
   * CONTEXT: Simple logging utility specific to GeoboundsSelectorModule
   * @namespace Logger
   */
  const Logger = {
    timestamp: function() {
      return new Date().toISOString();
    },
    log: function(message) {
      // console.log(`[${this.timestamp()}] [GeoboundsSelectorModule] ${message}`);
    },
    error: function(message, error) {
      // console.error(`[${this.timestamp()}] [GeoboundsSelectorModule] ERROR: ${message}`, error || '');
    },
    warn: function(message) {
      // console.warn(`[${this.timestamp()}] [GeoboundsSelectorModule] WARNING: ${message}`);
    },
    info: function(message) {
      // console.info(`[${this.timestamp()}] [GeoboundsSelectorModule] INFO: ${message}`);
    }
  };

  // Module state
  let modalInstance = null;
  let map = null;
  let rectangle = null;
  let initialBounds = null;

  /**
   * CONTEXT: Initializes the Google Map inside the modal
   * UPSTREAM: Called from openModal() when modal is shown
   * DOWNSTREAM: Creates map instance and calls initializeRectangle()
   * FAILURE: Logs error if map container not found
   * @private
   */
  function initializeMap() {
    const mapContainer = document.getElementById('geobounds-selector-map');

    // Get initial bounds from FilterModule or use sensible defaults
    const filterState = FilterModule.getFilterState();

    if (filterState.usergeoboundsforlist) {
      // User has set custom bounds - use those
      initialBounds = filterState.usergeoboundsforlist;
    } else {
      // No custom bounds set - try MapModule bounds, then fall back to Curacao defaults
      const mapBounds = MapModule.getBounds();

      // Validate that mapBounds are within Curacao region (not a dot in the ocean)
      const curacaoBounds = AppConfig.miniMapInFilter.boundsMap;
      const isValidBounds = mapBounds &&
        mapBounds.getSouthWest().lat() >= curacaoBounds.lat.lo - 0.5 &&
        mapBounds.getNorthEast().lat() <= curacaoBounds.lat.hi + 0.5 &&
        mapBounds.getSouthWest().lng() >= curacaoBounds.lng.lo - 0.5 &&
        mapBounds.getNorthEast().lng() <= curacaoBounds.lng.hi + 0.5;

      if (isValidBounds) {
        initialBounds = mapBounds;
      } else {
        // Fall back to Curacao default bounds from config
        initialBounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(curacaoBounds.lat.lo, curacaoBounds.lng.lo),
          new google.maps.LatLng(curacaoBounds.lat.hi, curacaoBounds.lng.hi)
        );
      }
    }

    // Create map centered on the initial bounds
    const center = initialBounds.getCenter();

    map = new google.maps.Map(mapContainer, {
      center: center,
      zoom: 12, // Will be adjusted in further initialization
      mapId: AppConfig.map.mapId,
      fullscreenControl: false,
      mapTypeControl: false,
      mapTypeControlOptions: AppConfig.map.mapTypeControlOptions,
      streetViewControl: false,
      zoomControl: true
    });

    // Initialize the rectangle and fit the map to it
    initializeRectangle();

    //scale map to fit rectangle nicely
    map.fitBounds(rectangle.getBounds());
  }

  /**
   * CONTEXT: Creates the draggable/resizable rectangle overlay on the map
   * UPSTREAM: Called from initializeMap() after map is created
   * DOWNSTREAM: Sets up rectangle with initial bounds
   * FAILURE: Logs error if map not initialized
   * @private
   */
  function initializeRectangle() {
 
    // Create rectangle with initial bounds
    rectangle = new google.maps.Rectangle({
      bounds: initialBounds,
      map: map,
      editable: true,
      draggable: true,
      strokeColor: '#1c7430', // Match the orange color from searchareaminimap
      strokeOpacity: 1,
      strokeWeight: 2,
      fillColor: '#495057',
      fillOpacity: 0.3,
      suppressUndo: true
    });
  }

  
  /**
   * CONTEXT: Opens the modal and initializes the map
   * UPSTREAM: Called from FilterModule when user clicks on #searchareaminimap
   * DOWNSTREAM: Shows Bootstrap modal and calls initializeMap()
   * FAILURE: Logs error if modal element not found
   * @public
   */
  function openModal() {
    const modalElement = document.getElementById('geoboundsModal');
    // Create Bootstrap modal instance if not already created
    if (!modalInstance) {
      modalInstance = new bootstrap.Modal(modalElement);
    }

    // Show the modal
    modalInstance.show();

    // Initialize map when modal is fully shown
    modalElement.addEventListener('shown.bs.modal', function onModalShown() {
      initializeMap();
      // Remove this listener after first run
      modalElement.removeEventListener('shown.bs.modal', onModalShown);
    }, { once: true });

  }

  /**
   * CONTEXT: Closes the modal and cleans up map resources
   * UPSTREAM: Called when user clicks Cancel or closes modal
   * DOWNSTREAM: Destroys map instance and removes references
   * FAILURE: Continues cleanup even if some steps fail
   * @private
   */
  function closeModal() {
    if (modalInstance) {
      modalInstance.hide();
    }

    // Clean up map resources
    if (map) {
      map = null;
    }
    if (rectangle) {
      rectangle.setMap(null);
      rectangle = null;
    }

  }

  /**
   * CONTEXT: Saves the current rectangle bounds to filterState and applies filters
   * UPSTREAM: Called when user clicks Apply button in modal
   * DOWNSTREAM: Updates FilterModule state, triggers filter refresh
   * FAILURE: If there is no valid rectangle yet (Apply clicked before the map
   *          finished initializing, or after the modal was already closed/cleaned
   *          up), falls back to "no custom area" (bounds = null) instead of throwing.
   * @private
   */
  function applyBounds() {
    // The rectangle can be missing when Apply fires before initializeRectangle()
    // has run (the Apply button is clickable during the modal's fade-in, before
    // the `shown.bs.modal` handler builds the map/rectangle) or after cleanup set
    // it back to null. getBounds() can also return undefined on an invalid shape.
    // In all those cases there is nothing drawn to apply, so treat it like the
    // out-of-Curacao case below: clear the custom area rather than crash.
    let bounds = (rectangle && rectangle.getBounds()) || null;

    if (bounds) {
      let ne = bounds.getNorthEast();
      let sw = bounds.getSouthWest();
      let topSelection = ne.lat();
      let leftSelection = sw.lng();
      let bottomSelection = sw.lat();
      let rightSelection = ne.lng();
      let topCuracao = AppConfig.miniMapInFilter.boundsMap.lat.hi;;
      let leftCuracao = AppConfig.miniMapInFilter.boundsMap.lng.lo;
      let bottomCuracao = AppConfig.miniMapInFilter.boundsMap.lat.lo;
      let rightCuracao = AppConfig.miniMapInFilter.boundsMap.lng.hi;

      //check if geobounds have an overlap with curacao as set in AppConfig.miniMapInFilter.boundsMap
      if(
        (topSelection > topCuracao && bottomSelection > topCuracao)||//selection is completely above curacao
        (topSelection < bottomCuracao && bottomSelection < bottomCuracao)||//selection is completely below curacao
        (leftSelection < leftCuracao && rightSelection < leftCuracao)||//selection is completely left of curacao
        (leftSelection > rightCuracao && rightSelection > rightCuracao)//selection is completely right of curacao
      ){ //the input doesn't overlap with curacao so let's ignore it
        bounds = null;
      }
    }

    // Update FilterModule's filterState
    const filterState = FilterModule.getFilterState();
    filterState.usergeoboundsforlist = bounds;

    // Close modal
    closeModal();

    // Update the minimap indicator and apply filters
    FilterModule.syncMiniMapSearchArea();
    FilterModule.applyFilters();
  }

  
  /**
   * CONTEXT: Sets up event listeners for modal buttons
   * UPSTREAM: Called from init() on module initialization
   * DOWNSTREAM: Wires up Apply, Clear, and Cancel buttons
   * FAILURE: Logs error if buttons not found
   * @private
   */
  function setupEventListeners() {
    // Apply button
    document.getElementById('geobounds-apply-btn').addEventListener('click', applyBounds);
 
    // Cancel button - just closes modal
    document.getElementById('geobounds-cancel-btn').addEventListener('click', closeModal);
  
    // Also handle modal close event for cleanup
    const modalElement = document.getElementById('geoboundsModal');
    if (modalElement) {
      modalElement.addEventListener('hidden.bs.modal', function() {
        // Clean up map resources when modal is hidden
        if (map) {
          map = null;
        }
        if (rectangle) {
          rectangle.setMap(null);
          rectangle = null;
        }
      });
    }
  }

  // Public interface
  return {
    /**
     * CONTEXT: Initializes the module by setting up event listeners
     * UPSTREAM: Called from main app initialization
     * DOWNSTREAM: Sets up all button event handlers
     * FAILURE: Logs errors but allows app to continue
     * @public
     */
    init: function() {
      setupEventListeners();
      Logger.info('GeoboundsSelectorModule initialized');
    },

    /**
     * CONTEXT: Opens the geobounds selector modal
     * UPSTREAM: Called from FilterModule when user clicks minimap
     * DOWNSTREAM: Shows modal and initializes map
     * FAILURE: Logs error and fails gracefully
     * @public
     */
    openModal: openModal
  };
})();

/**
 * Expose module globally on the window object
 */
window.GeoboundsSelectorModule = GeoboundsSelectorModule;
